/**
 * Type-safe wrappers for `/v1/events` (Sprint 4 Stage 1 backend, PR #110).
 *
 * Authenticated endpoints (manager actions) go through `customFetch`, which
 * injects the bearer JWT + tenant slug headers. The list endpoint
 * (GET /v1/events) is reachable both as a manager (any state) and from the
 * guest portal (published-only — backend filters by JWT role).
 *
 * Endpoints mounted in `internal/events/http_app/handler.go`:
 *   POST   /v1/events                        — create draft (201)
 *   GET    /v1/events                        — list (200; filters: from/to/category)
 *   GET    /v1/events/{id}                   — manager: any state (200)
 *   PATCH  /v1/events/{id}                   — update title/desc/dates (200)
 *   POST   /v1/events/{id}/publish           — draft|hidden → published (200)
 *   POST   /v1/events/{id}/hide              — published → hidden (200, body.reason)
 *   DELETE /v1/events/{id}                   — soft-delete (204)
 */
import { customFetch } from './openapi-fetch.js';
import type {
  Event,
  EventCreateInput,
  EventUpdateInput,
  EventHideInput,
  EventListFilters,
  EventListResponse,
  RsvpStatus,
  ScheduleListResponse,
  ScheduleItem,
  ScheduleItemInput,
} from '@hsc/types';

const BASE = '/events';

function toQuery(f: EventListFilters | undefined): Record<string, string> | undefined {
  if (!f) return undefined;
  const params: Record<string, string> = {};
  if (f.from !== undefined && f.from !== '') params['from'] = f.from;
  if (f.to !== undefined && f.to !== '') params['to'] = f.to;
  if (f.category !== undefined) params['category'] = f.category;
  return Object.keys(params).length === 0 ? undefined : params;
}

/** GET /v1/events — published events for guests, all states for managers. */
export async function listEvents(filters?: EventListFilters): Promise<EventListResponse> {
  const params = toQuery(filters);
  return customFetch<EventListResponse>(
    params ? { url: BASE, method: 'GET', params } : { url: BASE, method: 'GET' },
  );
}

/**
 * GET /v1/public/{slug}/events — guest-visible published events.
 *
 * Public endpoint mounted under `/v1/public/` (allowed by the default-deny
 * middleware per gitea #148/#162). No auth, slug-resolved tenant.
 */
export async function listPublicEvents(
  slug: string,
  filters?: EventListFilters,
): Promise<EventListResponse> {
  const params = toQuery(filters);
  const url = `/public/${encodeURIComponent(slug)}/events`;
  return customFetch<EventListResponse>(
    params ? { url, method: 'GET', params } : { url, method: 'GET' },
  );
}

/** GET /v1/events/{id} — manager-only (any state). */
export async function getEvent(id: string): Promise<Event> {
  return customFetch<Event>({ url: `${BASE}/${encodeURIComponent(id)}`, method: 'GET' });
}

/** POST /v1/events — create draft. */
export async function createEvent(input: EventCreateInput): Promise<Event> {
  return customFetch<Event>({ url: BASE, method: 'POST', data: input });
}

/** PATCH /v1/events/{id} — partial update (title / description / dates). */
export async function updateEvent(id: string, input: EventUpdateInput): Promise<Event> {
  return customFetch<Event>({
    url: `${BASE}/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: input,
  });
}

/** POST /v1/events/{id}/publish — draft|hidden → published. */
export async function publishEvent(id: string): Promise<Event> {
  return customFetch<Event>({
    url: `${BASE}/${encodeURIComponent(id)}/publish`,
    method: 'POST',
  });
}

/** POST /v1/events/{id}/hide — published → hidden (reason required). */
export async function hideEvent(id: string, input: EventHideInput): Promise<Event> {
  return customFetch<Event>({
    url: `${BASE}/${encodeURIComponent(id)}/hide`,
    method: 'POST',
    data: input,
  });
}

/** DELETE /v1/events/{id} — soft-delete (any state). */
export async function deleteEvent(id: string): Promise<void> {
  await customFetch<void>({
    url: `${BASE}/${encodeURIComponent(id)}`,
    method: 'DELETE',
  });
}

// --- RSVP (Волна 5 / A3 «Облака») — гостевая запись на мероприятие ---------

/** GET /v1/events/{id}/rsvp — статус записи гостя + занятость мест. */
export async function getRsvpStatus(id: string): Promise<RsvpStatus> {
  return customFetch<RsvpStatus>({
    url: `${BASE}/${encodeURIComponent(id)}/rsvp`,
    method: 'GET',
  });
}

/** POST /v1/events/{id}/rsvp — записаться (going / лист ожидания). */
export async function createRsvp(id: string): Promise<RsvpStatus> {
  return customFetch<RsvpStatus>({
    url: `${BASE}/${encodeURIComponent(id)}/rsvp`,
    method: 'POST',
  });
}

/** DELETE /v1/events/{id}/rsvp — отменить запись. */
export async function cancelRsvp(id: string): Promise<RsvpStatus> {
  return customFetch<RsvpStatus>({
    url: `${BASE}/${encodeURIComponent(id)}/rsvp`,
    method: 'DELETE',
  });
}


// --- Расписание дня (event_schedule, миграция 0066) — «время + текст» --------

/** GET /v1/events/schedule — строки расписания дня (authed; гость читает). */
export async function listSchedule(): Promise<ScheduleListResponse> {
  return customFetch<ScheduleListResponse>({ url: `${BASE}/schedule`, method: 'GET' });
}

/** POST /v1/events/schedule — добавить строку расписания (manager). */
export async function createScheduleItem(input: ScheduleItemInput): Promise<ScheduleItem> {
  return customFetch<ScheduleItem>({ url: `${BASE}/schedule`, method: 'POST', data: input });
}

/** DELETE /v1/events/schedule/{id} — удалить строку (manager). */
export async function deleteScheduleItem(id: string): Promise<void> {
  await customFetch<void>({
    url: `${BASE}/schedule/${encodeURIComponent(id)}`,
    method: 'DELETE',
  });
}
